import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';
import { useTranslation } from '../contexts/LanguageContext';
import { useMarket } from '../contexts/MarketContext';
import axios from 'axios';

const LOGO_URL = "/logo.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FacebookIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
);
const InstagramIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
);
const TikTokIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.12V9a6.33 6.33 0 00-.79-.05A6.34 6.34 0 003.15 15.3a6.34 6.34 0 0010.86 4.43v-7.15a8.16 8.16 0 005.58 2.2V11.3a4.85 4.85 0 01-3.77-1.84V6.69h3.77z"/></svg>
);
const LinkedInIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
);
const YouTubeIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
);

const socialConfig = [
  { key: 'facebook_url', icon: FacebookIcon, label: 'Facebook', hoverColor: 'hover:text-[#1877F2]' },
  { key: 'instagram_url', icon: InstagramIcon, label: 'Instagram', hoverColor: 'hover:text-[#E4405F]' },
  { key: 'youtube_url', icon: YouTubeIcon, label: 'YouTube', hoverColor: 'hover:text-[#FF0000]' },
  { key: 'tiktok_url', icon: TikTokIcon, label: 'TikTok', hoverColor: 'hover:text-white' },
  { key: 'linkedin_url', icon: LinkedInIcon, label: 'LinkedIn', hoverColor: 'hover:text-[#0A66C2]' },
];

export default function Footer() {
  const { t } = useTranslation();
  const { market, marketLink } = useMarket();
  const [settings, setSettings] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/site-settings`),
      axios.get(`${API}/properties`, { params: { market } })
    ]).then(([settRes, propRes]) => {
      setSettings(settRes.data);
      setProjects(propRes.data.slice(0, 5));
    }).catch(() => {});
  }, [market]);

  const address = settings?.company_address || 'гр. София, п.к 1700, ж.к. Студентски град, ул. "8-ми Декември" №21';
  const phone = settings?.company_phone || '+359 890 300 600';
  const email = settings?.company_email || 'office@greencastle.bg';
  const salesPhone = settings?.sales_phone || '+359 890 150 999';
  const salesEmail = settings?.sales_email || 'sales@greencastle.bg';

  const activeSocials = socialConfig.filter(s => settings?.[s.key]);

  return (
    <footer className="bg-[#1B5E37] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <div className="mb-6">
              <img src={LOGO_URL} alt="GreenCastle" className="h-16 w-auto object-contain brightness-0 invert" />
            </div>
            <p className="text-white/80 leading-relaxed">
              {t('footer.description')}
            </p>
            {activeSocials.length > 0 && (
              <div className="flex items-center gap-4 mt-6">
                {activeSocials.map((s) => (
                  <a
                    key={s.key}
                    href={settings[s.key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-white/60 ${s.hoverColor} transition-colors`}
                    title={s.label}
                    data-testid={`footer-${s.key}`}
                  >
                    <s.icon />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div>
            <h4 className="font-heading font-semibold text-lg mb-6">{t('footer.quick_links')}</h4>
            <ul className="space-y-3">
              {[
                { to: marketLink('/'), label: t('nav.home') },
                { to: marketLink('/services'), label: t('nav.services') },
                { to: marketLink('/properties'), label: t('footer.our_projects') },
                { to: marketLink('/listings'), label: t('nav.listings') || 'Обяви за имоти' },
                { to: marketLink('/about'), label: t('nav.about') },
                { to: marketLink('/contact'), label: t('footer.contacts') }
              ].map(link => (
                <li key={link.to}>
                  <Link to={link.to} className="text-white/80 hover:text-white transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-lg mb-6">{t('footer.current_projects')}</h4>
            <ul className="space-y-3">
              {projects.length > 0 ? (
                projects.map(p => (
                  <li key={p.id}>
                    <Link to={marketLink(`/properties/${p.id}`)} className="text-white/80 hover:text-white transition-colors">
                      {p.name}
                    </Link>
                  </li>
                ))
              ) : (
                <li className="text-white/60 text-sm">{t('footer.loading')}</li>
              )}
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-lg mb-6">{t('footer.contacts')}</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span className="text-white/80">{address}</span>
              </li>
              <li>
                <a href={`tel:${phone.replace(/\s/g, '')}`} className="flex items-center gap-3 text-white/80 hover:text-white transition-colors">
                  <Phone className="w-5 h-5" />
                  {phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${email}`} className="flex items-center gap-3 text-white/80 hover:text-white transition-colors">
                  <Mail className="w-5 h-5" />
                  {email}
                </a>
              </li>
            </ul>
            <h5 className="font-semibold mt-6 mb-3">{t('footer.sales_dept')}</h5>
            <ul className="space-y-2">
              <li>
                <a href={`tel:${salesPhone.replace(/\s/g, '')}`} className="flex items-center gap-3 text-white/80 hover:text-white transition-colors text-sm">
                  <Phone className="w-4 h-4" />
                  {salesPhone}
                </a>
              </li>
              <li>
                <a href={`mailto:${salesEmail}`} className="flex items-center gap-3 text-white/80 hover:text-white transition-colors text-sm">
                  <Mail className="w-4 h-4" />
                  {salesEmail}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <hr className="border-white/20 my-10" />

        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/60 text-sm">
            &copy; {new Date().getFullYear()} GreenCastle. {t('footer.rights')}
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-white/60 hover:text-white transition-colors text-sm">
              {t('footer.privacy')}
            </Link>
            <Link to="/terms" className="text-white/60 hover:text-white transition-colors text-sm">
              {t('footer.terms')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
